import { uniq } from "@-/com.npmjs.array-utils-ts";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  cardContentKindFromWordCardKind,
  cardReviewSettingsFromUser,
  isCardContentKindReviewEligible,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

export type SentenceDrillTokenCard = { fsrsCardId: string; studyCardId: string };

const SENTENCE_DRILL_CARD_TABLES = [
  "user_card",
  "fsrs_card",
  "user_word_sentence_card",
  "user",
] as const;

function wordKindForDrill(kind: DrillType["kind"]) {
  switch (kind) {
    case "word-sentence": {
      return "characters-in-context";
    }
    case "sentence-glyphs": {
      return "characters-in-sentence";
    }
    case "sentence-speech": {
      return "pronunciation-in-sentence";
    }
    case "meaning":
    case "pronunciation": {
      return null;
    }
  }
}

export function useGetSentenceDrillCardSetQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const userSettingsQueryOptions = actorQueries(
    import.meta.location,
  ).user.findFirst.useQueryOptions({});
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(SENTENCE_DRILL_CARD_TABLES),
  );
  return (tokenWords: readonly string[], drillTypeKind: DrillType["kind"]) =>
    withTableChangeVersion([
      "sentenceDrillCardSet",
      tokenWords,
      drillTypeKind,
      { actorDb, userSettingsQueryOptions },
    ] as const);
}

/**
 * Freeze the existing word-card identities behind this sentence's tokens. The
 * returned rows scale with the sentence, not the learner's deck, and need no
 * catalog hydration or full scheduling rows. Matching is on (word, drill kind): sentence tokens
 * carry no reading (`sentence_word` has none), and a word holds ONE reading once enrolled
 * (`findEnrolledWordPinyin` reuses it on every re-add), so each token names exactly one card.
 * Repeated tokens reuse that card.
 */
export const [useSentenceDrillCardSetQueryOptions, sentenceDrillCardSetQueryFn] =
  createUseQueryOptions(
    useGetSentenceDrillCardSetQueryKey,
    async ({
      client,
      queryKey: [_, tokenWords, drillTypeKind, { actorDb, userSettingsQueryOptions }],
    }): Promise<Map<number, SentenceDrillTokenCard>> => {
      const cards = new Map<number, SentenceDrillTokenCard>();
      const wordKind = wordKindForDrill(drillTypeKind);
      if (wordKind === null || tokenWords.length === 0) {
        return cards;
      }
      const settings = cardReviewSettingsFromUser(
        await client.fetchQuery(userSettingsQueryOptions),
      );
      if (
        !isCardContentKindReviewEligible({
          cardContentKind: cardContentKindFromWordCardKind(wordKind),
          settings,
        })
      ) {
        return cards;
      }

      const rows = await actorDb(import.meta.location).query.userCard.findMany({
        columns: { id: true },
        where: {
          fsrsCardId: { isNotNull: true },
          userWordSentenceCard: { kind: wordKind, word: { in: uniq([...tokenWords]) } },
        },
        with: {
          fsrsCard: { columns: { id: true } },
          userWordSentenceCard: { columns: { pinyin: true, word: true } },
        },
      });
      const cardByWord = new Map<string, SentenceDrillTokenCard>();
      for (const row of rows) {
        const overlay = row.userWordSentenceCard;
        // The previous content resolver excludes word cards without a reading.
        if (
          overlay === null ||
          row.fsrsCard === null ||
          overlay.pinyin === "" ||
          cardByWord.has(overlay.word)
        ) {
          continue;
        }
        cardByWord.set(overlay.word, { fsrsCardId: row.fsrsCard.id, studyCardId: row.id });
      }
      for (const [index, word] of tokenWords.entries()) {
        const card = cardByWord.get(word);
        if (card !== undefined) {
          cards.set(index, card);
        }
      }
      return cards;
    },
  );
